<script setup lang="ts">
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import Select from 'primevue/select';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import BaseIcon from '@/components/base/BaseIcon.vue';
import type { RiskProfileOption } from '@/composables/useKycData';
import { formatDateTime } from '@/utils/formatters';

type ReportSummary = RiskProfileOption['reports'][number];

type Badge = {
  label: string;
  class: string;
};

const props = withDefaults(defineProps<{
  selectedRiskProfileId: string | null;
  selectedReportRequestId: string | null;
  riskProfiles: RiskProfileOption[];
  selectedProfileReports: ReportSummary[];
  selectedReportMeta: ReportSummary | null;
  isGeneratingReport: boolean;
  profileConfigRoute: RouteLocationRaw | null;
  reportInstanceStory: string | null;
  isSelectedReportOnOldProfileVersion: boolean;
  formatReportDateTime: (dateValue?: string | null) => string;
  getReportVersionStatus: (report: ReportSummary) => Badge;
  getReportParecerBadge: (report: ReportSummary) => Badge;
  showReportSection?: boolean;
}>(), {
  showReportSection: true,
});

const emit = defineEmits<{
  'update:selectedRiskProfileId': [value: string | null];
  'update:selectedReportRequestId': [value: string | null];
  'request-new-report': [];
}>();

const selectedRiskProfileModel = computed({
  get: () => props.selectedRiskProfileId,
  set: (value: string | null) => emit('update:selectedRiskProfileId', value),
});

const selectedReportModel = computed({
  get: () => props.selectedReportRequestId,
  set: (value: string | null) => emit('update:selectedReportRequestId', value),
});

const selectedRiskProfile = computed(() =>
  props.riskProfiles.find((profile) => profile.id === props.selectedRiskProfileId) ?? null,
);

const canRequestNewReport = computed(() => {
  if (!props.selectedRiskProfileId) return false;
  return selectedRiskProfile.value?.can_request_report !== false;
});
</script>

<template>
  <aside
    :class="[
      'rounded-xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col',
      showReportSection ? 'h-full min-h-[176px]' : 'h-auto',
    ]"
  >
    <div class="flex justify-between items-center">
      <div class="text-xs font-semibold text-slate-500 uppercase tracking-[0.14em]">Política de Risco</div>
      <router-link
        v-if="profileConfigRoute"
        :to="profileConfigRoute"
        target="_blank"
        class="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1 text-sm font-medium"
        title="Configurar Política"
      >
        <span>Configurar</span>
        <BaseIcon icon="open_in_new" class="text-sm" />
      </router-link>
    </div>

    <div class="mt-1.5 flex items-center gap-1.5">
      <Select
        v-model="selectedRiskProfileModel"
        :options="riskProfiles"
        optionValue="id"
        placeholder="Selecione uma Política"
        class="min-w-0 flex-1"
        size="small"
        :disabled="isGeneratingReport"
      >
        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex items-center gap-2 min-w-0">
            <span class="truncate text-sm font-medium text-slate-800">{{ riskProfiles.find(p => p.id === slotProps.value)?.name }}</span>
            <Tag
              v-if="riskProfiles.find(p => p.id === slotProps.value)?.deleted_at"
              value="Excluída"
              severity="warn"
              class="shrink-0"
            />
          </div>
          <span v-else>{{ slotProps.placeholder }}</span>
        </template>
        <template #option="slotProps">
          <div class="flex items-center justify-between w-full gap-2">
            <div class="flex items-center gap-2 min-w-0">
              <span class="truncate mr-2">{{ slotProps.option.name }}</span>
              <Tag v-if="slotProps.option.deleted_at" value="Excluída" severity="warn" />
            </div>
            <span v-if="slotProps.option.reports?.length" class="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
          </div>
        </template>
      </Select>
    </div>

    <div
      v-if="selectedRiskProfile?.deleted_at"
      class="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      Esta política foi excluída. Os relatórios continuam disponíveis, mas novos relatórios não podem ser gerados com ela.
    </div>

    <template v-if="showReportSection">
      <div class="mt-2 flex justify-between items-center">
        <div class="text-xs font-semibold text-slate-500 uppercase tracking-[0.14em]">Relatório</div>
        <div class="flex items-center gap-1.5">
          <span v-if="selectedProfileReports.length" class="text-xs text-slate-500">
            {{ selectedProfileReports.length }} histórico
          </span>
          <Button
            label="Novo"
            size="small"
            severity="secondary"
            outlined
            class="!h-7 !px-2 !text-xs"
            :loading="isGeneratingReport"
            :disabled="!selectedRiskProfileId || isGeneratingReport || !canRequestNewReport"
            @click="emit('request-new-report')"
          >
            <template #icon>
              <BaseIcon class="text-sm">add</BaseIcon>
            </template>
          </Button>
        </div>
      </div>

      <div class="mt-1 flex items-center gap-1.5">
        <Select
          v-model="selectedReportModel"
          :options="selectedProfileReports"
          optionValue="request_id"
          placeholder="Selecione um relatório"
          class="min-w-0 flex-1"
          size="small"
          :disabled="!selectedProfileReports.length || isGeneratingReport"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value && selectedReportMeta" class="min-w-0 w-full">
              <div class="text-sm leading-tight font-semibold text-slate-800 truncate">{{ formatDateTime(selectedReportMeta.report_generated_at) }}</div>
            </div>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps">
            <div class="flex items-center justify-between w-full gap-2">
              <div class="min-w-0 text-sm font-medium text-slate-800 truncate">
                {{ formatReportDateTime(slotProps.option.report_generated_at) }}
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                <span :class="['text-[10px] px-2 py-0.5 rounded-full border font-semibold whitespace-nowrap', getReportVersionStatus(slotProps.option).class]">
                  {{ getReportVersionStatus(slotProps.option).label }}
                </span>
                <span :class="['text-[10px] px-2 py-0.5 rounded-full border font-semibold whitespace-nowrap', getReportParecerBadge(slotProps.option).class]">
                  {{ getReportParecerBadge(slotProps.option).label }}
                </span>
              </div>
            </div>
          </template>
        </Select>
      </div>

      <div v-if="selectedReportMeta" class="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span
          v-if="isSelectedReportOnOldProfileVersion"
          class="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
          title="Relatório gerado com versão anterior da política de risco"
        >
          <BaseIcon class="text-xs">warning</BaseIcon>
          Versão anterior
        </span>
        <span
          v-if="reportInstanceStory"
          class="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-100 text-slate-700 font-semibold"
        >
          {{ reportInstanceStory }}
        </span>
        <span :class="['text-[10px] px-2 py-0.5 rounded-full border font-semibold', getReportParecerBadge(selectedReportMeta).class]">
          {{ getReportParecerBadge(selectedReportMeta).label }}
        </span>
      </div>
    </template>
  </aside>
</template>
